import React, { useState } from 'react';

// パスワード表示切替の目アイコン【2026-07-22 追加】
const EyeIcon = ({ off }) => off ? (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="2" y1="2" x2="22" y2="22"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/></svg>
) : (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);

// サーバ側と同じルール（8〜128文字）でリアルタイム検証【2026-07-22 追加】
const passwordError = (pw) => {
  if (!pw) return '';
  if (pw.length < 8)   return `使用不可: パスワードは8文字以上にしてください（あと${8 - pw.length}文字）`;
  if (pw.length > 128) return '使用不可: パスワードは128文字以内にしてください';
  return '';
};

export default function Login({ onLogin }) {
  const [tab, setTab]       = useState('login'); // 'login' | 'register'
  const [form, setForm]     = useState({ student_id: '', password: '', name: '', email: '', role: 'student' });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  // 利用規約・プライバシーポリシーへの同意（新規登録時のみ）【2026-06-05 追加】
  const [agreed, setAgreed] = useState(false);
  // メール確認コード入力ステップ【2026-07-22 追加】
  //   signup 成功時はトークンが発行されない（verify-email 完了後に発行）ため、
  //   ここでコード入力を挟む。従来はこの画面が無く token=undefined を保存していた。
  const [verifyStep, setVerifyStep] = useState(false);
  const [code, setCode]             = useState('');
  const [info, setInfo]             = useState('');
  // パスワード表示切替【2026-07-22 追加】
  const [showPw, setShowPw] = useState(false);
  // パスワード再設定【2026-09-21 追加】
  //   /api/auth/password-reset/* は以前から実装されていたが画面が無く、
  //   運用上サーバから直接叩くしかなかったのでここに導線を作る。
  const [resetStep, setResetStep] = useState(''); // '' | 'request' | 'verify'
  const [newPw, setNewPw]         = useState('');
  const pwError    = passwordError(form.password);
  const newPwError = passwordError(newPw);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleLogin = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ student_id: form.student_id, password: form.password })
      });
      const data = await res.json();
      if (data.success && data.token) {
        onLogin(data.user, data.token);
      } else {
        setError(data.error || data.message || 'ログインに失敗しました');
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async e => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('パスワードは8文字以上にしてください'); return; }
    if (!agreed) { setError('利用規約とプライバシーポリシーに同意してください'); return; }
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/signup', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success && data.token) {
        // （現仕様では signup 直後にトークンは返らないが、将来の仕様変更に備える）
        onLogin(data.user, data.token);
      } else if (data.success) {
        // メール確認コード入力ステップへ【2026-07-22 追加】
        setVerifyStep(true);
        setError('');
        setInfo(data.code === 'MAIL_SEND_FAILED'
          ? 'メール送信に失敗しました。「コードを再送」を押してください。'
          : `${form.email} に確認コードを送信しました。10〜15分以内に入力してください。`);
      } else {
        setError(data.error || data.message || '登録に失敗しました');
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // メール確認コードの照合【2026-07-22 追加】
  const handleVerify = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/verify-email', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ student_id: form.student_id, code: code.trim() })
      });
      const data = await res.json();
      if (data.success && data.token) {
        onLogin(data.user, data.token);
      } else {
        setInfo('');
        let msg = data.error || data.message || '認証に失敗しました';
        if (typeof data.attemptsRemaining === 'number') {
          msg += `（残り${data.attemptsRemaining}回）`;
        }
        // コード期限切れ・試行上限は pending が消えるので登録からやり直し
        if (data.code === 'CODE_EXPIRED' || data.code === 'TOO_MANY_ATTEMPTS' || data.code === 'PENDING_NOT_FOUND') {
          setVerifyStep(false);
          setCode('');
        }
        setError(msg);
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // 確認コードの再送【2026-07-22 追加】
  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/resend-verification', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ student_id: form.student_id })
      });
      const data = await res.json();
      if (data.success) {
        setInfo('確認コードを再送しました。');
      } else {
        setError(data.error || data.message || '再送に失敗しました');
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // パスワード再設定[1]: 登録メアドに確認コードを送る【2026-09-21 追加】
  const handleResetRequest = async e => {
    e.preventDefault();
    setError(''); setInfo('');
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/password-reset/request', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ studentId: form.student_id.trim() })
      });
      const data = await res.json();
      if (data.success) {
        // 列挙対策でサーバは常に成功を返す（存在しない学籍番号でもここに来る）
        //   → 届かないときにIDの打ち間違いを疑えるよう、メールアドレスでの再試行を案内する【2026-10-20】
        setResetStep('verify');
        setInfo(`該当するアカウントがあれば、登録メールアドレスに確認コードを送信しました。${data.expiresInMinutes || 15}分以内に入力してください。` +
                '数分待っても届かない場合は、入力したIDが登録時と違っている可能性があります。「コードを送り直す」から、大学のメールアドレスで試してください。');
      } else {
        setError(data.error || data.message || '送信に失敗しました');
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // パスワード再設定[2]: コードを照合して新パスワードを設定【2026-09-21 追加】
  const handleResetVerify = async e => {
    e.preventDefault();
    setError('');
    if (newPwError) { setError(newPwError.replace('使用不可: ', '')); return; }
    setLoading(true);
    try {
      const res  = await fetch('/api/auth/password-reset/verify', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          studentId:   form.student_id.trim(),
          code:        code.trim(),
          newPassword: newPw
        })
      });
      const data = await res.json();
      if (data.success && data.token) {
        // サーバが自動ログイン用トークンを返すので、そのままログインさせる
        onLogin(data.user, data.token);
      } else {
        setInfo('');
        let msg = data.error || data.message || 'パスワードの再設定に失敗しました';
        if (typeof data.attemptsRemaining === 'number') {
          msg += `（残り${data.attemptsRemaining}回）`;
        }
        // 期限切れ・試行上限はコードが無効化されるので送信からやり直し
        if (data.code === 'CODE_EXPIRED' || data.code === 'TOO_MANY_ATTEMPTS') {
          setResetStep('request');
          setCode(''); setNewPw('');
        }
        setError(msg);
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // 再設定モードを抜ける【2026-09-21 追加】
  const exitReset = () => {
    setResetStep(''); setCode(''); setNewPw(''); setInfo(''); setError('');
  };

  const s = {
    wrap: {
      minHeight:      '100vh',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      background:     'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
      padding:        '1rem'
    },
    legalFooter: {
      marginTop: '1.25rem',
      fontSize:  '0.8rem',
      color:     'rgba(255,255,255,0.85)',
      textAlign: 'center'
    },
    legalLink: {
      color:          'rgba(255,255,255,0.95)',
      textDecoration: 'underline',
      margin:         '0 0.5rem'
    },
    card: {
      background:   '#fff',
      borderRadius: '16px',
      padding:      '2.5rem 2rem',
      width:        '100%',
      maxWidth:     '420px',
      boxShadow:    '0 20px 60px rgba(0,0,0,0.2)'
    },
    logo: {
      textAlign:    'center',
      marginBottom: '2rem'
    },
    logoIcon: { fontSize: '3rem', display: 'block', marginBottom: '0.5rem' },
    logoTitle: { fontSize: '1.4rem', fontWeight: '700', color: '#1e3a8a', margin: 0 },
    logoSub:   { fontSize: '0.85rem', color: '#888', margin: '0.25rem 0 0 0' },
    tabs: {
      display:      'flex',
      background:   '#f3f4f6',
      borderRadius: '8px',
      padding:      '4px',
      marginBottom: '1.5rem'
    },
    tabBtn: (active) => ({
      flex:         1,
      padding:      '0.5rem',
      border:       'none',
      borderRadius: '6px',
      cursor:       'pointer',
      fontWeight:   '600',
      fontSize:     '0.9rem',
      background:   active ? '#fff' : 'transparent',
      color:        active ? '#1e3a8a' : '#888',
      boxShadow:    active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
      transition:   'all 0.2s'
    }),
    field: { marginBottom: '1rem' },
    label: { display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#555', marginBottom: '0.35rem' },
    input: {
      width:        '100%',
      padding:      '0.65rem 0.9rem',
      border:       '1.5px solid #e5e7eb',
      borderRadius: '8px',
      fontSize:     '1rem',
      boxSizing:    'border-box',
      outline:      'none',
      transition:   'border-color 0.2s'
    },
    select: {
      width:        '100%',
      padding:      '0.65rem 0.9rem',
      border:       '1.5px solid #e5e7eb',
      borderRadius: '8px',
      fontSize:     '1rem',
      boxSizing:    'border-box',
      background:   '#fff',
      cursor:       'pointer'
    },
    error: {
      background:   '#fdecea',
      color:        '#c62828',
      border:       '1px solid #ef9a9a',
      borderRadius: '8px',
      padding:      '0.65rem 0.9rem',
      marginBottom: '1rem',
      fontSize:     '0.9rem'
    },
    notice: {
      background:   '#eff6ff',
      border:       '1px solid #bfdbfe',
      borderRadius: '8px',
      padding:      '0.65rem 0.9rem',
      marginBottom: '1rem',
      fontSize:     '0.85rem',
      color:        '#1e40af',
      lineHeight:   1.5
    },
    pwWrap: { position: 'relative' },
    pwToggle: {
      position:   'absolute',
      right:      '0.5rem',
      top:        '50%',
      transform:  'translateY(-50%)',
      background: 'none',
      border:     'none',
      padding:    '0.25rem',
      cursor:     'pointer',
      color:      '#888',
      display:    'flex',
      alignItems: 'center'
    },
    pwErr: { color: '#dc2626', fontSize: '0.8rem', margin: '0.35rem 0 0 0' },
    btn: (disabled) => ({
      width:        '100%',
      padding:      '0.8rem',
      background:   disabled ? '#93c5fd' : '#2563eb',
      color:        '#fff',
      border:       'none',
      borderRadius: '8px',
      fontSize:     '1rem',
      fontWeight:   '700',
      cursor:       disabled ? 'not-allowed' : 'pointer',
      marginTop:    '0.5rem',
      transition:   'background 0.2s'
    })
  };

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>
          <span style={{ ...s.logoIcon, color: '#1e3a8a', display: 'flex', justifyContent: 'center' }}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 9.5 12 4 2 9.5l10 5.5z"/><path d="M6 12.5V17c0 1.66 2.69 3 6 3s6-1.34 6-3v-4.5"/><path d="M22 9.5V15"/></svg>
          </span>
          <p style={s.logoTitle}>出席管理システム</p>
          <p style={s.logoSub}>Attendance Management System</p>
        </div>

        {/* Android端末向け案内【2026-07-21】 */}
        {/Android/i.test(navigator.userAgent) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: '10px', padding: '0.7rem 0.9rem', marginBottom: '1.25rem'
          }}>
            <span style={{ flexShrink: 0, display: 'flex', color: '#1e40af' }} aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>
            </span>
            <p style={{ margin: 0, fontSize: '0.84rem', color: '#1e40af', lineHeight: 1.5 }}>
              ログインすると<strong>Android用出席管理アプリ</strong>をダウンロードできます。
            </p>
          </div>
        )}

        {!verifyStep && !resetStep && (
          <div style={s.tabs}>
            <button style={s.tabBtn(tab === 'login')}    onClick={() => { setTab('login');    setError(''); }}>ログイン</button>
            <button style={s.tabBtn(tab === 'register')} onClick={() => { setTab('register'); setError(''); }}>新規登録</button>
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        {verifyStep ? (
          /* メール確認コード入力【2026-07-22 追加】 */
          <form onSubmit={handleVerify}>
            {info && (
              <div style={{
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
                padding: '0.65rem 0.9rem', marginBottom: '1rem',
                fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.5
              }}>
                {info}
              </div>
            )}
            <div style={s.field}>
              <label style={s.label}>確認コード（6桁）</label>
              <input style={s.input} name="code" value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="123456" required
                inputMode="numeric" autoComplete="one-time-code" maxLength={10}
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <button type="submit" style={s.btn(loading)} disabled={loading}>
              {loading ? '確認中...' : '認証してログイン'}
            </button>
            <button type="button" onClick={handleResend} disabled={loading}
              style={{ ...s.btn(loading), background: 'transparent', color: '#2563eb', fontWeight: '600' }}>
              コードを再送
            </button>
            <button type="button"
              onClick={() => { setVerifyStep(false); setCode(''); setInfo(''); setError(''); }}
              disabled={loading}
              style={{ ...s.btn(false), background: 'transparent', color: '#888', fontWeight: '500', marginTop: 0 }}>
              ← 登録画面に戻る
            </button>
          </form>
        ) : resetStep ? (
          /* パスワード再設定【2026-09-21 追加】 */
          resetStep === 'request' ? (
            <form onSubmit={handleResetRequest}>
              <div style={s.notice}>
                ログインID（学籍番号）か、登録したメールアドレスを入力すると、
                <strong>登録されているメールアドレス</strong>宛てに確認コード（6桁）とログインIDを送ります。<br/>
                IDを忘れた場合は、大学のメールアドレスを入力してください。<br/>
                メールが届かない場合は、迷惑メールフォルダもご確認ください。<br/>
                ※ 送り直しは<strong>60秒</strong>あけてください（それより短い間隔では送信されません）。
              </div>
              <div style={s.field}>
                <label style={s.label}>ログインID（学籍番号）またはメールアドレス</label>
                <input style={s.input} name="student_id" value={form.student_id} onChange={handleChange}
                  placeholder="S2025001 または yamada@university.ac.jp" required autoFocus
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
              </div>
              <button type="submit" style={s.btn(loading)} disabled={loading}>
                {loading ? '送信中...' : '確認コードを送る'}
              </button>
              <button type="button" onClick={exitReset} disabled={loading}
                style={{ ...s.btn(false), background: 'transparent', color: '#888', fontWeight: '500', marginTop: 0 }}>
                ← ログインに戻る
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetVerify}>
              {info && <div style={s.notice}>{info}</div>}
              <div style={s.field}>
                <label style={s.label}>確認コード（6桁）</label>
                <input style={s.input} value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder="123456" required autoFocus
                  inputMode="numeric" autoComplete="one-time-code" maxLength={10}
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
              </div>
              <div style={s.field}>
                <label style={s.label}>新しいパスワード（8文字以上）</label>
                <div style={s.pwWrap}>
                  <input style={{ ...s.input, paddingRight: '2.8rem', ...(newPwError ? { borderColor: '#dc2626' } : {}) }}
                    type={showPw ? 'text' : 'password'} value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    placeholder="••••••••" required minLength={8} autoComplete="new-password"
                    onFocus={e => e.target.style.borderColor = newPwError ? '#dc2626' : '#2563eb'}
                    onBlur={e  => e.target.style.borderColor = newPwError ? '#dc2626' : '#e5e7eb'} />
                  <button type="button" style={s.pwToggle} onClick={() => setShowPw(v => !v)}
                    aria-label={showPw ? 'パスワードを隠す' : 'パスワードを表示'}>
                    <EyeIcon off={showPw} />
                  </button>
                </div>
                {newPwError && <p style={s.pwErr}>{newPwError}</p>}
              </div>
              <button type="submit" style={s.btn(loading)} disabled={loading}>
                {loading ? '設定中...' : 'パスワードを設定してログイン'}
              </button>
              <button type="button" disabled={loading}
                onClick={() => { setResetStep('request'); setCode(''); setNewPw(''); setError(''); setInfo(''); }}
                style={{ ...s.btn(loading), background: 'transparent', color: '#2563eb', fontWeight: '600' }}>
                コードを送り直す
              </button>
              <button type="button" onClick={exitReset} disabled={loading}
                style={{ ...s.btn(false), background: 'transparent', color: '#888', fontWeight: '500', marginTop: 0 }}>
                ← ログインに戻る
              </button>
            </form>
          )
        ) : tab === 'login' ? (
          <form onSubmit={handleLogin}>
            <div style={s.field}>
              <label style={s.label}>学籍番号 / ID</label>
              <input style={s.input} name="student_id" value={form.student_id} onChange={handleChange}
                placeholder="S2025001" required
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div style={s.field}>
              <label style={s.label}>パスワード</label>
              <div style={s.pwWrap}>
                <input style={{ ...s.input, paddingRight: '2.8rem' }} name="password" type={showPw ? 'text' : 'password'} value={form.password} onChange={handleChange}
                  placeholder="••••••••" required
                  onFocus={e => e.target.style.borderColor = '#2563eb'}
                  onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
                <button type="button" style={s.pwToggle} onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? 'パスワードを隠す' : 'パスワードを表示'}>
                  <EyeIcon off={showPw} />
                </button>
              </div>
            </div>
            <button type="submit" style={s.btn(loading)} disabled={loading}>
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>
            {/* パスワード再設定への導線【2026-09-21 追加】 */}
            <button type="button" disabled={loading}
              onClick={() => { setResetStep('request'); setError(''); setInfo(''); }}
              style={{ ...s.btn(false), background: 'transparent', color: '#2563eb',
                       fontWeight: '600', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              ID・パスワードを忘れた方はこちら
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div style={s.field}>
              <label style={s.label}>氏名</label>
              <input style={s.input} name="name" value={form.name} onChange={handleChange}
                placeholder="山田 太郎" required
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div style={s.field}>
              <label style={s.label}>学籍番号 / ID</label>
              <input style={s.input} name="student_id" value={form.student_id} onChange={handleChange}
                placeholder="S2025001" required
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div style={s.field}>
              <label style={s.label}>メールアドレス</label>
              <input style={s.input} name="email" type="email" value={form.email} onChange={handleChange}
                placeholder="yamada@university.ac.jp" required
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e  => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div style={s.field}>
              <label style={s.label}>パスワード（8文字以上）</label>
              <div style={s.pwWrap}>
                <input style={{ ...s.input, paddingRight: '2.8rem', ...(pwError ? { borderColor: '#dc2626' } : {}) }}
                  name="password" type={showPw ? 'text' : 'password'} value={form.password} onChange={handleChange}
                  placeholder="••••••••" required minLength={8}
                  onFocus={e => e.target.style.borderColor = pwError ? '#dc2626' : '#2563eb'}
                  onBlur={e  => e.target.style.borderColor = pwError ? '#dc2626' : '#e5e7eb'} />
                <button type="button" style={s.pwToggle} onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? 'パスワードを隠す' : 'パスワードを表示'}>
                  <EyeIcon off={showPw} />
                </button>
              </div>
              {/* 一文字打つごとに使用不可理由を赤文字表示【2026-07-22 追加】 */}
              {pwError && <p style={s.pwErr}>{pwError}</p>}
            </div>
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '0.65rem 0.9rem',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: '#1e40af',
              lineHeight: 1.5
            }}>
              ℹ️ このフォームは <strong>学生用</strong> です。<br/>
              教員の方は管理者から発行された <strong>招待 URL</strong> から登録してください。
            </div>
            {/* 規約同意チェック【2026-06-05 追加】 */}
            <label style={{
              display:      'flex',
              alignItems:   'flex-start',
              gap:          '0.5rem',
              marginBottom: '1rem',
              fontSize:     '0.85rem',
              color:        '#374151',
              cursor:       'pointer',
              lineHeight:   1.5
            }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={{ marginTop: '0.2rem', width: '16px', height: '16px', flexShrink: 0 }}
              />
              <span>
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>利用規約</a>
                および
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>プライバシーポリシー</a>
                に同意します
              </span>
            </label>
            {/* 未同意でも押せるようにし、onSubmit 側でエラー表示する（disabled だと無反応で分かりにくいため）【2026-07-22】 */}
            <button type="submit" style={s.btn(loading)} disabled={loading}>
              {loading ? '登録中...' : '学生アカウントを作成'}
            </button>
          </form>
        )}
      </div>

      {/* 利用規約・プライバシーポリシーへの導線【2026-06-05 追加】 */}
      <div style={s.legalFooter}>
        <a style={s.legalLink} href="/terms"   target="_blank" rel="noopener noreferrer">利用規約</a>
        ・
        <a style={s.legalLink} href="/privacy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
      </div>
    </div>
  );
}
